import { Component, DestroyRef, computed, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PageEvent, MatPaginatorModule } from '@angular/material/paginator';
import { MatTableModule } from '@angular/material/table';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { EMPTY, Subscription, catchError, finalize, switchMap, timer } from 'rxjs';
import { OuiButtonModule } from 'omnium-ui/button';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { OuiTableModule } from 'omnium-ui/table';
import { OuiTagModule } from 'omnium-ui/tag';
import { OuiTooltipModule } from 'omnium-ui/tooltip';
import { appRoutePaths } from '../../../../app.paths';
import {
  ActionAuthorizationService,
  doublonsBusterActionCodes
} from '../../../../core/auth/action-authorization.service';
import {
  ExPostCampaignDetail,
  ExPostDocumentsPage,
  ExPostEmailBatch
} from '../../../../core/models/api.models';
import { DoublonsBusterApiService } from '../../../../core/services/doublons-buster-api.service';
import { StateCardComponent } from '../../shared/state-card/state-card.component';
import { SummaryGridComponent, SummaryGridItem } from '../../shared/summary-grid/summary-grid.component';

@Component({
  selector: 'dbw-couts-ex-post-campaign-detail-page',
  imports: [
    MatPaginatorModule,
    MatTableModule,
    RouterModule,
    OuiButtonModule,
    OuiTableModule,
    OuiTagModule,
    OuiTooltipModule,
    StateCardComponent,
    SummaryGridComponent
  ],
  templateUrl: './couts-ex-post-campaign-detail.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./couts-ex-post-campaign-detail.component.scss']
})
export class CoutsExPostCampaignDetailPageComponent {
  private static readonly POLLING_INTERVAL_MS = 5000;

  readonly batchColumns = ['batchNumber', 'plannedCount', 'progress', 'status', 'startedAt', 'actions'];
  readonly errorColumns = ['client', 'consultant', 'deliveryMode', 'status', 'errorMessage'];
  readonly pageSizeOptions = [10, 25, 50];

  readonly campaignId: number;
  readonly detail = signal<ExPostCampaignDetail | undefined>(undefined);
  readonly loading = signal(true);
  readonly error = signal<string | undefined>(undefined);

  readonly startingGeneration = signal(false);
  readonly sendingBatch = signal(false);
  readonly resendingBatchId = signal<number | null>(null);
  readonly deletingCampaign = signal(false);
  readonly downloading = signal<'Email' | 'Courrier' | null>(null);
  readonly printingCourrier = signal(false);

  readonly errorDocuments = signal<ExPostDocumentsPage | undefined>(undefined);
  readonly errorPageIndex = signal(0);
  readonly errorPageSize = signal(10);

  readonly appRoutePaths = appRoutePaths;

  readonly canGenerate = computed(
    () =>
      this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.exPostCampaignGenerate) &&
      this.isGenerationAllowed() &&
      !this.startingGeneration()
  );

  readonly canSendBatch = computed(
    () =>
      this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.exPostEmailBatchSend) &&
      this.isBatchSendingAllowed() &&
      !this.sendingBatch()
  );

  readonly canDownload = computed(() =>
    this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.exPostDocumentsDownload)
  );

  /**
   * Aucun PDF n'est téléchargeable tant que la génération n'est pas terminée :
   * un ZIP partiel donnerait des bordereaux manquants sans le signaler.
   */
  readonly canDownloadDocuments = computed(() => this.canDownload() && this.isGenerated());

  readonly canDeleteCampaign = computed(
    () =>
      this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.exPostCampaignImport) &&
      this.detail()?.campaign.status !== 'Generating' &&
      !this.hasSendingBatch() &&
      !this.deletingCampaign()
  );

  readonly isGenerationAllowed = computed(() => {
    const status = this.detail()?.campaign.status;
    return status === 'Imported' || status === 'GeneratedWithErrors';
  });

  readonly isGenerated = computed(() => {
    const status = this.detail()?.campaign.status;
    return status === 'Generated' || status === 'GeneratedWithErrors';
  });

  readonly nextPlannedBatch = computed(
    () => this.detail()?.emailBatches.find(batch => batch.status === 'Planned')
  );

  readonly hasSendingBatch = computed(
    () => this.detail()?.emailBatches.some(batch => batch.status === 'Sending') ?? false
  );

  /**
   * Le quota d'envoi glissant ne laisse pas passer le prochain lot : le serveur
   * a daté la sortie de fenêtre des envois manquants.
   */
  readonly quotaBlocksNextBatch = computed(() => {
    const nextAllowedAtUtc = this.detail()?.emailQuota.nextBatchAllowedAtUtc;
    return !!nextAllowedAtUtc && new Date(nextAllowedAtUtc).getTime() > Date.now();
  });

  readonly isBatchSendingAllowed = computed(
    () =>
      this.isGenerated() &&
      !!this.nextPlannedBatch() &&
      !this.hasSendingBatch() &&
      !this.quotaBlocksNextBatch()
  );

  readonly sendBatchTooltip = computed(() => {
    if (!this.isGenerated()) {
      return 'Les bordereaux doivent être générés avant l’envoi des emails.';
    }

    if (this.hasSendingBatch()) {
      return 'Un lot est déjà en cours d’envoi.';
    }

    if (!this.nextPlannedBatch()) {
      return 'Tous les lots ont été envoyés.';
    }

    if (this.quotaBlocksNextBatch()) {
      const quota = this.detail()!.emailQuota;
      return (
        `Quota d’envoi atteint (${quota.sentInWindow}/${quota.limit} emails sur 24h) : ` +
        `ce lot en demande ${quota.nextBatchSize}, il n’en reste que ${quota.remaining}. ` +
        `Envoi possible à partir du ${this.formatDate(quota.nextBatchAllowedAtUtc)}.`
      );
    }

    return '';
  });

  readonly downloadTooltip = computed(() => {
    if (this.isGenerated()) {
      return '';
    }

    return this.detail()?.campaign.status === 'Generating'
      ? 'Téléchargement possible une fois la génération des bordereaux terminée.'
      : 'Les bordereaux doivent être générés avant tout téléchargement.';
  });

  readonly summaryItems = computed<SummaryGridItem[]>(() => {
    const detail = this.detail();

    if (!detail) {
      return [];
    }

    return [
      {
        key: 'email',
        label: 'Bordereaux mail',
        value: detail.emailDocumentCount,
        meta: `${detail.sentCount} envoyé(s)`
      },
      {
        key: 'courrier',
        label: 'Bordereaux courrier',
        value: detail.courrierDocumentCount,
        meta: 'À imprimer et poster'
      },
      {
        key: 'generated',
        label: 'PDF générés',
        value: `${detail.generatedCount} / ${detail.emailDocumentCount + detail.courrierDocumentCount}`,
        compactValue: true
      },
      {
        key: 'consultants',
        label: 'Consultants',
        value: detail.consultantCount
      },
      {
        key: 'batches',
        label: "Lots d'envoi",
        value: detail.emailBatches.length,
        meta: '9500 mails max par lot'
      },
      {
        key: 'errors',
        label: 'Erreurs',
        value: detail.generationFailedCount + detail.sendFailedCount,
        aside:
          detail.generationFailedCount + detail.sendFailedCount > 0
            ? { label: 'À vérifier', status: 'error' }
            : { label: 'OK', status: 'completed' }
      }
    ];
  });

  private readonly destroyRef = inject(DestroyRef);
  private pollingSubscription?: Subscription;

  constructor(
    private readonly apiService: DoublonsBusterApiService,
    private readonly actionAuthorizationService: ActionAuthorizationService,
    private readonly snackbarService: OuiSnackbarService,
    private readonly router: Router,
    route: ActivatedRoute
  ) {
    this.campaignId = Number(route.snapshot.paramMap.get('campaignId'));
    this.load(true);
    this.loadErrorDocuments();

    this.destroyRef.onDestroy(() => this.stopPolling());
  }

  reload(): void {
    this.load(true);
    this.loadErrorDocuments();
  }

  startGeneration(): void {
    if (!this.canGenerate()) {
      return;
    }

    this.startingGeneration.set(true);

    this.apiService
      .startExPostGeneration(this.campaignId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.startingGeneration.set(false))
      )
      .subscribe({
        next: () => {
          this.snackbarService.open('La génération des bordereaux a été lancée.', 'success');
          this.load();
        },
        error: httpError => {
          this.snackbarService.open(
            httpError?.error?.detail ?? 'Le lancement de la génération a échoué.',
            'error'
          );
        }
      });
  }

  sendNextBatch(): void {
    if (!this.canSendBatch()) {
      return;
    }

    const batch = this.nextPlannedBatch();
    const confirmationMessage =
      `Envoyer le lot n°${batch?.batchNumber} (${batch?.plannedCount} email(s)) ? ` +
      'Le prochain lot ne pourra être envoyé que 24h plus tard.';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    this.sendingBatch.set(true);

    this.apiService
      .sendNextExPostEmailBatch(this.campaignId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.sendingBatch.set(false))
      )
      .subscribe({
        next: sentBatch => {
          this.snackbarService.open(
            `L'envoi du lot n°${sentBatch.batchNumber} (${sentBatch.plannedCount} emails) a démarré.`,
            'success'
          );
          this.load();
        },
        error: httpError => {
          this.snackbarService.open(
            httpError?.error?.detail ?? "Le déclenchement de l'envoi a échoué.",
            'error'
          );
        }
      });
  }

  isBatchResendable(batch: ExPostEmailBatch): boolean {
    return (
      (batch.status === 'Completed' || batch.status === 'CompletedWithErrors') &&
      (batch.sentCount < batch.plannedCount || batch.failedCount > 0)
    );
  }

  canResendBatch(batch: ExPostEmailBatch): boolean {
    return (
      this.actionAuthorizationService.canExecute(doublonsBusterActionCodes.exPostEmailBatchSend) &&
      this.isGenerated() &&
      this.isBatchResendable(batch) &&
      !this.hasSendingBatch() &&
      this.quotaAllowsResend(batch) &&
      this.resendingBatchId() === null
    );
  }

  /**
   * Une relance ne renvoie que les emails restants : elle est jugée sur son
   * propre volume, pas sur celui du prochain lot planifié. Le serveur applique
   * le même quota, ce test ne fait qu'éviter un aller-retour perdu.
   */
  private quotaAllowsResend(batch: ExPostEmailBatch): boolean {
    const quota = this.detail()?.emailQuota;

    return !quota || batch.plannedCount - batch.sentCount <= quota.remaining;
  }

  resendBatch(batch: ExPostEmailBatch): void {
    if (!this.canResendBatch(batch)) {
      return;
    }

    const remainingCount = batch.plannedCount - batch.sentCount;
    const confirmationMessage =
      `Relancer le lot n°${batch.batchNumber} (${remainingCount} email(s) restant(s)) ? ` +
      'Les emails déjà envoyés ne seront pas renvoyés.';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    this.resendingBatchId.set(batch.id);

    this.apiService
      .resendExPostEmailBatch(this.campaignId, batch.id)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.resendingBatchId.set(null))
      )
      .subscribe({
        next: resentBatch => {
          this.snackbarService.open(
            `La relance du lot n°${resentBatch.batchNumber} a démarré.`,
            'success'
          );
          this.load();
        },
        error: httpError => {
          this.snackbarService.open(
            httpError?.error?.detail ?? 'La relance du lot a échoué.',
            'error'
          );
        }
      });
  }

  deleteCampaign(): void {
    if (!this.canDeleteCampaign()) {
      return;
    }

    const year = this.detail()?.campaign.year;
    const confirmationMessage =
      `Supprimer définitivement la campagne ${year} ? ` +
      'Tous les documents, bordereaux PDF et lots d’envoi seront perdus.';

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    this.deletingCampaign.set(true);

    this.apiService
      .deleteExPostCampaign(this.campaignId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.deletingCampaign.set(false))
      )
      .subscribe({
        next: () => {
          this.snackbarService.open(`La campagne ${year} a été supprimée.`, 'success');
          this.router.navigateByUrl(appRoutePaths.controleGestion.coutsExPost);
        },
        error: httpError => {
          this.snackbarService.open(
            httpError?.error?.detail ?? 'La suppression de la campagne a échoué.',
            'error'
          );
        }
      });
  }

  downloadArchive(deliveryMode: 'Email' | 'Courrier'): void {
    if (!this.canDownloadDocuments() || this.downloading()) {
      return;
    }

    this.downloading.set(deliveryMode);

    this.apiService
      .downloadExPostArchive(this.campaignId, deliveryMode)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.downloading.set(null))
      )
      .subscribe({
        next: blob => {
          const year = this.detail()?.campaign.year ?? '';
          this.triggerBlobDownload(
            blob,
            `couts-ex-post-${year}-${deliveryMode === 'Email' ? 'mail' : 'courrier'}.zip`
          );
        },
        error: () => {
          this.snackbarService.open('Le téléchargement des bordereaux a échoué.', 'error');
        }
      });
  }

  printCourrierDocuments(): void {
    if (!this.canDownloadDocuments() || this.printingCourrier()) {
      return;
    }

    this.printingCourrier.set(true);

    this.apiService
      .printExPostArchive(this.campaignId, 'Courrier')
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.printingCourrier.set(false))
      )
      .subscribe({
        next: blob => this.triggerBlobPrint(blob),
        error: () => {
          this.snackbarService.open("La préparation de l'impression a échoué.", 'error');
        }
      });
  }

  onErrorPageChange(event: PageEvent): void {
    this.errorPageIndex.set(event.pageIndex);
    this.errorPageSize.set(event.pageSize);
    this.loadErrorDocuments();
  }

  getBatchStatusLabel(batch: ExPostEmailBatch): string {
    switch (batch.status) {
      case 'Planned':
        return 'Planifié';
      case 'Sending':
        return 'Envoi en cours';
      case 'Completed':
        return 'Terminé';
      case 'CompletedWithErrors':
        return 'Terminé avec erreurs';
      default:
        return batch.status;
    }
  }

  getCampaignStatusLabel(): string {
    switch (this.detail()?.campaign.status) {
      case 'Imported':
        return 'Importée — bordereaux à générer';
      case 'Generating':
        return 'Génération des bordereaux en cours';
      case 'Generated':
        return 'Bordereaux générés';
      case 'GeneratedWithErrors':
        return 'Générée avec erreurs';
      case 'Superseded':
        return 'Remplacée par un nouvel import';
      default:
        return this.detail()?.campaign.status ?? '';
    }
  }

  formatDate(value?: string | null): string {
    if (!value) {
      return 'Non renseigné';
    }

    return new Intl.DateTimeFormat('fr-FR', {
      dateStyle: 'short',
      timeStyle: 'short'
    }).format(new Date(value));
  }

  private load(forceLoadingIndicator = false): void {
    if (forceLoadingIndicator && !this.detail()) {
      this.loading.set(true);
    }

    this.apiService
      .getExPostCampaignDetail(this.campaignId)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(httpError => {
          this.error.set(httpError?.error?.detail ?? 'Impossible de charger la campagne.');
          this.loading.set(false);
          return EMPTY;
        })
      )
      .subscribe(detail => {
        this.detail.set(detail);
        this.error.set(undefined);
        this.loading.set(false);
        this.syncPolling(detail);
      });
  }

  private loadErrorDocuments(): void {
    this.apiService
      .getExPostDocumentsPage(this.campaignId, this.errorPageIndex(), this.errorPageSize(), {
        errorsOnly: true
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(() => EMPTY)
      )
      .subscribe(page => this.errorDocuments.set(page));
  }

  private syncPolling(detail: ExPostCampaignDetail): void {
    const isActive =
      detail.campaign.status === 'Generating' ||
      detail.emailBatches.some(batch => batch.status === 'Sending');

    if (isActive && !this.pollingSubscription) {
      this.pollingSubscription = timer(
        CoutsExPostCampaignDetailPageComponent.POLLING_INTERVAL_MS,
        CoutsExPostCampaignDetailPageComponent.POLLING_INTERVAL_MS
      )
        .pipe(
          switchMap(() => this.apiService.getExPostCampaignDetail(this.campaignId)),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: polledDetail => {
            this.detail.set(polledDetail);

            const stillActive =
              polledDetail.campaign.status === 'Generating' ||
              polledDetail.emailBatches.some(batch => batch.status === 'Sending');

            if (!stillActive) {
              this.stopPolling();
              this.loadErrorDocuments();
            }
          },
          error: () => this.stopPolling()
        });
    } else if (!isActive) {
      this.stopPolling();
    }
  }

  private stopPolling(): void {
    this.pollingSubscription?.unsubscribe();
    this.pollingSubscription = undefined;
  }

  private triggerBlobDownload(blob: Blob, fileName: string): void {
    const downloadUrl = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = downloadUrl;
    anchor.download = fileName;
    anchor.click();
    URL.revokeObjectURL(downloadUrl);
  }

  private triggerBlobPrint(blob: Blob): void {
    const printUrl = URL.createObjectURL(blob);
    const printFrame = document.createElement('iframe');
    printFrame.style.display = 'none';
    printFrame.src = printUrl;

    const cleanup = () => {
      printFrame.remove();
      URL.revokeObjectURL(printUrl);
    };

    printFrame.addEventListener('load', () => {
      const contentWindow = printFrame.contentWindow;
      if (!contentWindow) {
        cleanup();
        return;
      }

      // L'impression est asynchrone (boîte de dialogue OS) : on ne peut
      // libérer l'iframe/l'URL qu'une fois celle-ci fermée.
      contentWindow.addEventListener('afterprint', cleanup, { once: true });
      contentWindow.focus();
      contentWindow.print();
    });

    document.body.appendChild(printFrame);
  }
}
